import { lazy } from "react";
import { PathConstants } from "@/routes/constants";
import { createBrowserRouter } from "react-router-dom";
import Layout from "@/modules/common/components/layout/layout";
import { ThemeProvider } from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import RouteGuard from "@/modules/common/components/wrappers/RouteGuard";
import {
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import { AnalyticsProvider } from "@/modules/analytics/context/analytics.context";
import AmbassadorsPage from "@/pages/ambassadors";
import Targets2 from "@/pages/targets2";
import NewLeadPage from "@/modules/lead-page/new";
import { NotificationCenterProvider } from "@/modules/notification-center/context/notification-center.context";
import PropertyTrackPage from "@/pages/property-track-page";

// Lazy load the page component chunks on demand when required
const LeadsPage = lazy(() => import("../pages/leads-page"));
const NotFound = lazy(() => import("../pages/404"));
const Lead = lazy(() => import("../pages/lead"));
const NextActions = lazy(() => import("../pages/next-actions"));
const Sales = lazy(() => import("../pages/sales"));
const CommentsSearch = lazy(() => import("../pages/comments-search"));
const SalesCommissions = lazy(() => import("../pages/sales-commissions"));
const NawyNowCalculator = lazy(() => import("../pages/move-now-calculator"));
const AgentsActivity = lazy(() => import("../pages/agents-activity"));
const AgentsTargets = lazy(
  () => import("../modules/agents/components/agents-targets/agents-targets"),
);
const SettingsCategories = lazy(() => import("../pages/settings-categories"));
const ListingDashboard = lazy(() => import("../pages/listings-dashboard"));
const FacebookRules = lazy(() => import("../pages/facebook-rules"));
const LeadIconsSettings = lazy(() => import("../pages/lead-icons-settings"));
const PaymentPlansSettings = lazy(
  () => import("../pages/payment-plans-settings"),
);
const Pools = lazy(() => import("../pages/pools"));
const UsersSettings = lazy(() => import("../pages/users-settings"));
const StatusPage = lazy(
  () => import("../modules/statuses/components/statuses-page/status-page"),
);
const ConfigurationsSettings = lazy(
  () => import("../pages/configurations-settings"),
);
const FunnelsSettings = lazy(() => import("../pages/funnels-settings"));
const Commissions = lazy(() => import("../pages/iframe/commissions"));
const Dashboards = lazy(() => import("../pages/iframe/dashboards"));
const League = lazy(() => import("../pages/iframe/league"));
const NawyInventory = lazy(() => import("../pages/iframe/nawy-inventory"));
const DailyOperations = lazy(() => import("../pages/iframe/daily-operations"));
const StatusesGlossary = lazy(
  () => import("../pages/iframe/statuses-glossary"),
);
const MarketUpdates = lazy(() => import("../pages/iframe/market-updates"));
const NawyLogin = lazy(() => import("../pages/auth/nawy-login"));
const FranchiseLogin = lazy(() => import("../pages/auth/crm-franchise-login"));
const ChangePassword = lazy(
  () => import("../pages/auth/forgot-password/change-password"),
);
const SendEmail = lazy(
  () => import("../pages/auth/forgot-password/send-email"),
);
const ResetPassword = lazy(() => import("../pages/auth/reset-password"));
const AuthCallback = lazy(() => import("../modules/login/auth-callback"));
const AuthChallengeForm = lazy(() => import("../pages/auth/auth-challenge"));
const AutoDelegationWeights = lazy(
  () => import("../pages/auto-delegation-weights"),
);
const AgentsTree = lazy(() => import("../pages/agents-tree"));
const TargetsConfigurations = lazy(
  () => import("../pages/targets-configurations"),
);
const DisplayOrganizationsTargets = lazy(
  () => import("../pages/display-organizations-targets"),
);
const AutoAssignmentPools = lazy(
  () => import("../pages/auto-assignment-pools"),
);
const LeadTagsSettings = lazy(() => import("../pages/lead-tags-settings"));
const DeveloperTracksSettings = lazy(
  () => import("../pages/settings")
);
const NotificationsCenter = lazy(() => import("../pages/notifications-center"));
const Clients = lazy(() => import("../pages/clients"));
const PropertyTracks = lazy(() => import("../pages/property-tracks"));
const ClientsPage = lazy(() => import("../pages/clients-page"));
const PropertyTrackSettings = lazy(
  () => import("../pages/property-track-settings"),
);
const PropertyTracksEdit = lazy(
  () => import("../pages/property-track-settings/edit"),
);
const InterestConfiguartion = lazy(
  () => import("../pages/interests-configuration/index"),
);
const InterestActionsConfiguartion = lazy(
  () => import("../pages/interests-configuration/show"),
);
const TaskDashboard = lazy(() => import("../pages/tasks-dashboard"));
const ExpoPage = lazy(() => import("../pages/expo"));
const ZoomCallsPage = lazy(() => import("../pages/zoom-calls"));
const AiConfigurationsPage = lazy(
  () =>
    import("../modules/ai-configurations/components/ai-configurations-page"),
);

const NewHome = lazy(() => import("../pages/home"));
const HomeWrapper = lazy(() => import("../pages/home/wrapper"));

const ReviewsPage = lazy(() => import("../pages/reviews"));
const LeadAccessLinkPage = lazy(() => import("../pages/lead-access-link"));
const RentalInventoryPage = lazy(() => import("../pages/rental-inventory"));

type Route = {
  path: string;
  element: React.ReactNode;
  allowedPermissions?: { name: PermissionsEnum; level: PermissionLevel }[];
  allowedTiers?: OrganizationTierTypes[];
};

export const routes: Route[] = [
  { path: PathConstants.HOME, element: <HomeWrapper /> }, //common
  { path: PathConstants.LEADS, element: <LeadsPage /> }, //common
  { path: PathConstants.LEAD, element: <Lead /> }, //common
  { path: PathConstants.LEAD_NEW, element: <NewLeadPage /> }, //common
  { path: PathConstants.NEXT_ACTIONS, element: <NextActions /> }, //common
  { path: PathConstants.CLIENTS, element: <Clients /> }, //common
  {
    path: PathConstants.PROPERTY_TRACKS,
    element: <PropertyTracks />,
    allowedPermissions: [
      { name: PermissionsEnum.TRACKS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.CLIENT,
    element: <ClientsPage />,
  }, //common
  {
    path: PathConstants.AGENTS_TREE,
    element: <AgentsTree />,
    allowedPermissions: [
      { name: PermissionsEnum.AGENTS_TREE, level: PermissionLevel.READ },
    ],
  },
  { path: PathConstants.SALES, element: <Sales /> }, //common
  {
    path: PathConstants.COMMENTS_SEARCH,
    element: <CommentsSearch />,
    allowedPermissions: [
      { name: PermissionsEnum.SEARCH_COMMENTS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.LISTING_DASHBOARD,
    element: <ListingDashboard />,
  },
  {
    path: PathConstants.SALES_COMMISSIONS,
    element: <SalesCommissions />,
    allowedPermissions: [
      { name: PermissionsEnum.AGENTS_COMMISSIONS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.SALES_COMMISSIONS_REDIRECTION,
    element: <SalesCommissions />,
  },
  {
    path: PathConstants.AGENTS_ACTIVITY,
    element: <AgentsActivity />,
    allowedPermissions: [
      { name: PermissionsEnum.AGENTS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.AGENTS_TARGETS,
    element: <AgentsTargets />,
    allowedPermissions: [
      { name: PermissionsEnum.AGENTS, level: PermissionLevel.READ },
    ],
  },

  {
    path: PathConstants.AUTO_DELEGATION_WEIGHTS,
    element: <AutoDelegationWeights />,
    allowedPermissions: [
      { name: PermissionsEnum.AGENTS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.NAWY_CALCULATOR,
    element: <NawyNowCalculator />,
    allowedPermissions: [
      {
        name: PermissionsEnum.NAWY_NOW_CALCULATOR,
        level: PermissionLevel.READ,
      },
      {
        name: PermissionsEnum.NAWY_UNLOCKED_CALCULATOR,
        level: PermissionLevel.READ,
      },
    ],
    allowedTiers: [OrganizationTierTypes.NAWY],
  },
  {
    path: PathConstants.FACEBOOK_RULES,
    element: <FacebookRules />,
    allowedPermissions: [
      { name: PermissionsEnum.FACEBOOK_RULES, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.POOLS,
    element: <Pools />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.AUTO_ASSIGNMENT_POOLS,
    element: <AutoAssignmentPools />,
  },
  {
    path: PathConstants.USERS,
    element: <UsersSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.USERS_PAGE, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.STATUSES,
    element: <StatusPage />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },

  {
    path: PathConstants.CATEGORIES,
    element: <SettingsCategories />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },

  { path: PathConstants.LEAD_ICONS, element: <LeadIconsSettings /> }, //common
  {
    path: PathConstants.LEAD_TAGS,
    element: <LeadTagsSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.LEAD_TAGS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.SETTINGS,
    element: <DeveloperTracksSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.SETTINGS_PAGE, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.PAYMENT_PLANS,
    element: <PaymentPlansSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.CONFIGURATIONS,
    element: <ConfigurationsSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.FUNNELS,
    element: <FunnelsSettings />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.COMMISSIONS,
    element: <Commissions />,
  },
  {
    path: PathConstants.DASHBOARDS,
    element: <Dashboards />,
    allowedPermissions: [
      { name: PermissionsEnum.DASHBOARDS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.LEAGUE,
    element: <League />,
    allowedPermissions: [
      { name: PermissionsEnum.SALES_LEAGUE, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.DAILY_OPERATIONS,
    element: <DailyOperations />,
    allowedPermissions: [
      { name: PermissionsEnum.DAILY_OPERATIONS, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.NAWY_INVENTORY,
    element: <NawyInventory />,
  },
  {
    path: PathConstants.STATUSES_GLOSSARY,
    element: <StatusesGlossary />,
  },
  {
    path: PathConstants.MARKET_UPDATES,
    element: <MarketUpdates />,
  },

  { path: PathConstants.LOGIN, element: <NawyLogin /> },
  { path: PathConstants.FRANCHISE_LOGIN, element: <FranchiseLogin /> },
  { path: PathConstants.SEND_EMAIL_FRANCHISE, element: <SendEmail /> },
  {
    path: PathConstants.CHANGE_PASSWORD_FRANCHISE,
    element: <ChangePassword />,
  },
  { path: PathConstants.RESET_PASSWORD, element: <ResetPassword /> },
  { path: PathConstants.AUTH_CALLBACK, element: <AuthCallback /> },
  { path: PathConstants.AUTH_CHALLENGE, element: <AuthChallengeForm /> },
  {
    path: PathConstants.SELECTED_TARGET_CONFIGURATIONS,
    element: <TargetsConfigurations />,
    allowedPermissions: [
      {
        name: PermissionsEnum.TARGETS_CONFIGURATIONS_PAGE,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    path: PathConstants.TARGET_CONFIGURATIONS,
    element: <TargetsConfigurations />,
    allowedPermissions: [
      {
        name: PermissionsEnum.TARGETS_CONFIGURATIONS_PAGE,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    path: PathConstants.DISPLAY_ORGANIZATIONS_TARGETS,
    element: <DisplayOrganizationsTargets />,
    allowedPermissions: [
      {
        name: PermissionsEnum.TARGETS_CONFIGURATIONS_PAGE,
        level: PermissionLevel.READ,
      },
    ],
  },
  {
    path: PathConstants.AMBASSADORS,
    element: <AmbassadorsPage />,
  },
  {
    path: PathConstants.TARGET_2,
    element: <Targets2 />,
  },
  {
    path: "/new-lead/:uid",
    element: <NewLeadPage />,
  },
  {
    path: PathConstants.NOTIFICATIONS_CENTER,
    element: <NotificationsCenter />,
  },
  {
    path: PathConstants.PROPERTY_TRACK_SETTINGS,
    element: <PropertyTrackSettings />,
  },
  {
    path: PathConstants.PROPERTY_TRACK_SETTINGS_EDIT,
    element: <PropertyTracksEdit />,
  },
  {
    path: PathConstants.PROPERTY_TRACK,
    element: <PropertyTrackPage />,
  },
  {
    path: PathConstants.RENTAL_INVENTORY,
    element: <RentalInventoryPage />,
  },
  {
    path: PathConstants.INTERESTS_CONFIGURATION,
    element: <InterestConfiguartion />,
  },
  {
    path: PathConstants.INTEREST_ACTIONS_CONFIGURATION,
    element: <InterestActionsConfiguartion />,
  },
  {
    path: PathConstants.TASK_DASHBOARD,
    element: <TaskDashboard />,
  },
  {
    path: PathConstants.EXPO_DASHBOARD,
    element: <ExpoPage />,
    allowedTiers: [OrganizationTierTypes.NAWY],
  },
  {
    path: PathConstants.ZOOM_CALLS,
    element: <ZoomCallsPage />,
    allowedPermissions: [
      { name: PermissionsEnum.ZOOM_CALLS_ADMIN, level: PermissionLevel.READ },
    ],
  },
  {
    path: PathConstants.AI_CONFIGURATIONS,
    element: <AiConfigurationsPage />,
    allowedPermissions: [
      { name: PermissionsEnum.COMMON_SETTINGS, level: PermissionLevel.READ },
    ],
  },
  { path: PathConstants.NEW_HOME, element: <NewHome /> },
  { path: PathConstants.REVIEWS, element: <ReviewsPage /> },
  {
    path: PathConstants.LEAD_ACCESS_LINK,
    element: <LeadAccessLinkPage />,
  },
];

const router = createBrowserRouter([
  {
    element: (
      <AnalyticsProvider>
        <NotificationCenterProvider>
          <Layout />
        </NotificationCenterProvider>
      </AnalyticsProvider>
    ),
    errorElement: <NotFound />,
    children: routes.map((route) => ({
      ...route,
      element: route.allowedPermissions ? (
        <RouteGuard
          allowedPermissions={route.allowedPermissions}
          allowedTiers={route.allowedTiers}
        >
          {route.element}
        </RouteGuard>
      ) : (
        route.element
      ),
    })),
  },
  {
    path: PathConstants.LISTING_DASHBOARD_EMBED,
    element: (
      <ThemeProvider theme={nawyTheme}>
        <ListingDashboard />
      </ThemeProvider>
    ),
  },
]);

export default router;
